import React from 'react';
import Box from '@mui/material/Box';
import {
  GridRow,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import type {
  GridDimensions,
  GridRowId,
  GridRowProps,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';

import colors from '../../common/colors';
import DataGrid from '../Wrappers/DataGrid';
import type { DataGridProps } from '../Wrappers/DataGrid/types';
import styles, {
  BAND_CLASS,
  DIVIDER_COLOR_VAR,
  DIVIDER_ROW_CLASS,
  GRID_WRAPPER_CLASS,
  ROW_CLICK_IGNORE_CLASS,
  SECTION_CLASS,
  STICKY_DETAIL_PANEL_CLASS,
} from './styles';
import type { SectionedDataGridProps, SectionedDataGridSection } from './types';

const DEFAULT_BORDER_RADIUS = '4px';
const DEFAULT_HEADER_BAND_COLOR = colors.grey_300;
const DEFAULT_HEADER_BACKGROUND_COLOR = colors.neutral_400;

/**
 * How long the scroll to a newly expanded row keeps re-aiming, in frames. Long
 * enough to outlast the grid's asynchronous panel measurement.
 */
const SCROLL_CORRECTION_FRAMES = 30;

type RowDividerColorResolver = (row: GridValidRowModel) => string | undefined;

/** Viewers who ask for reduced motion get a jump instead of an animation. */
const scrollBehavior = (): ScrollBehavior =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
    ? 'auto'
    : 'smooth';

/**
 * Where a scroll to this row would land: its place in the document, and the
 * page height that caps how far the page can scroll. Read independent of the
 * current scroll position, so it changes only when the layout moves the row.
 */
const scrollDestination = (row: HTMLElement) =>
  `${row.getBoundingClientRect().top + window.scrollY}/${
    document.documentElement.scrollHeight
  }`;

/**
 * Feeds the active `getRowDividerColor` resolver to the module-level
 * `DividerRow` slot. Kept out of the render body so the row component identity
 * stays stable across renders — rows never remount as the resolver changes.
 */
const RowDividerColorContext = React.createContext<
  RowDividerColorResolver | undefined
>(undefined);

/**
 * Row slot that paints a data-driven divider rule. When the active resolver
 * returns a color for the row, it tags the row with `DIVIDER_ROW_CLASS` and
 * exposes the color through `DIVIDER_COLOR_VAR`; otherwise the row is rendered
 * untouched.
 */
const DividerRow = React.forwardRef<HTMLDivElement, GridRowProps>(
  ({ row, className, style, ...rest }, ref) => {
    const resolveDividerColor = React.useContext(RowDividerColorContext);
    const dividerColor = resolveDividerColor?.(row);

    return (
      <GridRow
        {...rest}
        row={row}
        ref={ref}
        className={[className, dividerColor && DIVIDER_ROW_CLASS]
          .filter(Boolean)
          .join(' ')}
        style={
          dividerColor
            ? ({
                ...style,
                [DIVIDER_COLOR_VAR]: dividerColor,
              } as React.CSSProperties)
            : style
        }
      />
    );
  }
);
DividerRow.displayName = 'SectionedDataGridDividerRow';

const dividerRowSlots = { row: DividerRow };

/** Width of the grid's visible viewport, pinned sections included. */
const getStickyDetailPanelWidth = (dimensions: GridDimensions) =>
  dimensions.viewportInnerSize.width +
  dimensions.leftPinnedWidth +
  dimensions.rightPinnedWidth;

/**
 * Pins detail-panel content against the grid's horizontal scroll. The grid
 * renders each panel inside its scroller at full row width, so the content
 * would otherwise slide along with the columns. This wrapper sticks at the
 * viewport's left edge, sized to the visible width and kept in sync through
 * the grid's resize event — so the columns scroll while the panel stays put.
 */
const StickyDetailPanel = ({ children }: { children: React.ReactNode }) => {
  const apiRef = useGridApiContext();
  const [width, setWidth] = React.useState(() =>
    getStickyDetailPanelWidth(apiRef.current.getRootDimensions())
  );

  React.useEffect(() => {
    const updateWidth = () =>
      setWidth(getStickyDetailPanelWidth(apiRef.current.getRootDimensions()));

    // Re-measure on mount: the grid's first layout pass can land between the
    // state initializer above and this subscription.
    updateWidth();

    return apiRef.current.subscribeEvent(
      'viewportInnerSizeChange',
      updateWidth
    );
  }, [apiRef]);

  return (
    <Box
      className={STICKY_DETAIL_PANEL_CLASS}
      sx={styles.stickyDetailPanel}
      // Inline style, not sx: the width changes on every grid resize and an
      // sx value would mint a new emotion class each time.
      style={{ width }}
    >
      {children}
    </Box>
  );
};

type SectionGridProps<R extends GridValidRowModel> = Pick<
  SectionedDataGridProps<R>,
  | 'columns'
  | 'headerBandColor'
  | 'headerBandSx'
  | 'gridSx'
  | 'getRowClassName'
  | 'noRowsMessage'
  | 'headerBackgroundColor'
  | 'tableBackgroundColor'
  | 'autosize'
  | 'autosizeOptions'
  | 'getDetailPanelContent'
  | 'getDetailPanelHeight'
  | 'onDetailPanelExpandedRowIdsChange'
  | 'slots'
  | 'localeText'
  | 'pinnedColumns'
  | 'getRowHeight'
> & {
  section: SectionedDataGridSection<R>;
  /** The open row, when it is one of this section's; `null` otherwise. */
  expandedRowId: GridRowId | null;
  /** Reports this section's new open row, or `null` when it closes one. */
  onExpandedRowIdChange: (rowId: GridRowId | null) => void;
};

/**
 * A single titled section: a coloured header band above its own DataGrid.
 * Which row is open is owned by the stack, not the section, so that only one
 * panel is open across every section at a time.
 */
const SectionGrid = <R extends GridValidRowModel>({
  section,
  columns,
  headerBandColor,
  headerBandSx,
  gridSx,
  getRowClassName,
  noRowsMessage,
  headerBackgroundColor,
  tableBackgroundColor,
  autosize,
  autosizeOptions,
  getDetailPanelContent,
  getDetailPanelHeight,
  onDetailPanelExpandedRowIdsChange,
  slots,
  localeText,
  pinnedColumns,
  getRowHeight,
  expandedRowId,
  onExpandedRowIdChange,
}: SectionGridProps<R>) => {
  const apiRef = useGridApiRef();

  const detailPanelExpandedRowIds = React.useMemo(
    () => (expandedRowId != null ? [expandedRowId] : []),
    [expandedRowId]
  );

  const handleExpandedRowIdsChange = React.useCallback(
    (ids: Array<GridRowId>) => {
      const nextExpandedRowId = ids.find((id) => id !== expandedRowId) ?? null;
      onExpandedRowIdChange(nextExpandedRowId);

      if (nextExpandedRowId != null) {
        onDetailPanelExpandedRowIdsChange?.(nextExpandedRowId);
      }
    },
    [expandedRowId, onExpandedRowIdChange, onDetailPanelExpandedRowIdsChange]
  );

  // Brings the row that just opened to the top of the page, so its panel fills
  // the space below instead of opening off-screen. The scroll re-aims for a
  // short window rather than firing once, because the row is still moving when
  // it opens: the grid measures auto-height panels asynchronously, so for a few
  // frames the page is its old height and a panel collapsing above this row
  // still takes up its space. Frames that find the row where it was left issue
  // nothing, so a viewer who scrolls away mid-animation is not fought.
  React.useEffect(() => {
    if (expandedRowId == null) {
      return undefined;
    }

    let frame = 0;
    let framesLeft = SCROLL_CORRECTION_FRAMES;
    let lastDestination: string | null = null;

    const alignRow = () => {
      const row = apiRef.current?.getRowElement(expandedRowId);
      const destination = row ? scrollDestination(row) : null;

      if (destination !== null && destination !== lastDestination) {
        lastDestination = destination;
        row?.scrollIntoView?.({ block: 'start', behavior: scrollBehavior() });
      }

      framesLeft -= 1;
      frame = framesLeft > 0 ? requestAnimationFrame(alignRow) : 0;
    };

    frame = requestAnimationFrame(alignRow);

    return () => cancelAnimationFrame(frame);
  }, [apiRef, expandedRowId]);

  // Panel content is wrapped so it stays pinned while this grid scrolls
  // horizontally. Non-element content passes through unwrapped — the grid
  // decides a row has no panel (and hides its toggle) by that check.
  const getStickyDetailPanelContent = React.useCallback<
    NonNullable<SectionGridProps<R>['getDetailPanelContent']>
  >(
    (params) => {
      const content = getDetailPanelContent?.(params);
      return React.isValidElement(content) ? (
        <StickyDetailPanel>{content}</StickyDetailPanel>
      ) : (
        content
      );
    },
    [getDetailPanelContent]
  );

  // A click anywhere on the row toggles its panel, matching the chevron.
  const handleRowClick = React.useCallback<
    NonNullable<DataGridProps['onRowClick']>
  >(
    (params, event) => {
      const target = event.target as HTMLElement;
      // The chevron toggles the panel itself; skip it so a chevron click doesn't
      // toggle twice. Cells opting out via ROW_CLICK_IGNORE_CLASS keep their own
      // click behaviour instead of expanding the row.
      if (
        target.closest(`.${ROW_CLICK_IGNORE_CLASS}`) ||
        target.closest('.MuiDataGrid-detailPanelToggleCell')
      ) {
        return;
      }
      apiRef.current?.toggleDetailPanel(params.id);
    },
    [apiRef]
  );

  // Controlled master-detail props are only supplied when the caller renders
  // detail content; otherwise the grid behaves as a plain table.
  const detailPanelProps = getDetailPanelContent
    ? {
        getDetailPanelContent: getStickyDetailPanelContent,
        getDetailPanelHeight,
        detailPanelExpandedRowIds,
        onDetailPanelExpandedRowIdsChange: handleExpandedRowIdsChange,
        onRowClick: handleRowClick,
      }
    : {};

  return (
    <Box
      className={SECTION_CLASS}
      data-testid={`sectioned-data-grid-section-${section.id}`}
    >
      <Box
        className={BAND_CLASS}
        sx={[
          styles.band,
          { backgroundColor: headerBandColor },
          ...(Array.isArray(headerBandSx) ? headerBandSx : [headerBandSx]),
        ]}
      >
        {section.title}
      </Box>
      <Box className={GRID_WRAPPER_CLASS} sx={styles.gridWrapper}>
        <DataGrid
          apiRef={apiRef}
          columns={section.columns ?? columns}
          rows={section.rows}
          getRowClassName={getRowClassName}
          noRowsMessage={noRowsMessage}
          sx={gridSx}
          headerBackgroundColor={headerBackgroundColor}
          tableBackgroundColor={tableBackgroundColor}
          autosize={autosize}
          autosizeOptions={autosizeOptions}
          localeText={localeText}
          slots={slots}
          pinnedColumns={pinnedColumns}
          getRowHeight={getRowHeight}
          {...detailPanelProps}
        />
      </Box>
    </Box>
  );
};

/**
 * Stack of titled DataGrid sections rendered as one continuous panel.
 *
 * Each section is a coloured header band plus a natural-height grid (all
 * rows visible, no internal scroll). Sections stack flush; only the first
 * band and the last grid get rounded corners. Master-detail props are
 * forwarded to every section's grid, enabling accordion-style row panels —
 * one open at a time across the whole stack, so opening a row in one section
 * closes whatever was open in any other.
 */
const SectionedDataGrid = <R extends GridValidRowModel = GridValidRowModel>({
  sections,
  columns,
  headerBandColor = DEFAULT_HEADER_BAND_COLOR,
  headerBandSx,
  gridSx,
  getRowClassName,
  getRowDividerColor,
  noRowsMessage,
  borderRadius = DEFAULT_BORDER_RADIUS,
  headerBackgroundColor = DEFAULT_HEADER_BACKGROUND_COLOR,
  tableBackgroundColor,
  autosize,
  autosizeOptions,
  getDetailPanelContent,
  getDetailPanelHeight,
  onDetailPanelExpandedRowIdsChange,
  slots,
  localeText,
  pinnedColumns,
  getRowHeight,
}: SectionedDataGridProps<R>) => {
  const gridSlots = getRowDividerColor
    ? { ...slots, ...dividerRowSlots }
    : slots;

  // The one open panel in the stack, held here rather than in each section so
  // that opening a row closes the previous one whichever section it was in.
  // Sections are keyed alongside the row because row ids need only be unique
  // within their own section.
  const [expanded, setExpanded] = React.useState<{
    sectionId: SectionedDataGridSection['id'];
    rowId: GridRowId;
  } | null>(null);

  return (
    <RowDividerColorContext.Provider
      value={getRowDividerColor as RowDividerColorResolver | undefined}
    >
      <Box
        sx={styles.container(borderRadius)}
        data-testid="sectioned-data-grid"
      >
        {sections.map((section) => (
          <SectionGrid<R>
            key={section.id}
            section={section}
            columns={columns}
            headerBandColor={headerBandColor}
            headerBandSx={headerBandSx}
            gridSx={gridSx}
            getRowClassName={getRowClassName}
            noRowsMessage={noRowsMessage}
            headerBackgroundColor={headerBackgroundColor}
            tableBackgroundColor={tableBackgroundColor}
            autosize={autosize}
            autosizeOptions={autosizeOptions}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            onDetailPanelExpandedRowIdsChange={
              onDetailPanelExpandedRowIdsChange
            }
            slots={gridSlots}
            localeText={localeText}
            pinnedColumns={pinnedColumns}
            getRowHeight={getRowHeight}
            expandedRowId={
              expanded?.sectionId === section.id ? expanded.rowId : null
            }
            onExpandedRowIdChange={(rowId) =>
              setExpanded(
                rowId == null ? null : { sectionId: section.id, rowId }
              )
            }
          />
        ))}
      </Box>
    </RowDividerColorContext.Provider>
  );
};

export default SectionedDataGrid;
